import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Slider } from "primereact/slider";
import styled from "styled-components";
import { theme } from "styles/theme";
import { trackEvent } from "utils/analytics";

const ModalContent = styled.div`
  padding: 1rem 0;

  .p-slider-range {
    background-color: ${theme.colors.tertiary};
  }

  .p-slider-handle {
    background-color: ${theme.colors.tertiaryLight};
    border: 2px solid ${theme.colors.tertiaryDark};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: ${theme.colors.textPrimary};
`;

const SpeedSliderContainer = styled.div`
  margin-top: 1.5rem;
`;

const SpeedValue = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  margin: 1rem 0;
  color: ${theme.colors.tertiary};
`;

const TimeFieldsContainer = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TimeField = styled.div`
  flex: 1;

  .p-calendar {
    width: 100%;
  }
`;

interface ScheduleEditModalProps {
  visible: boolean;
  onHide: () => void;
  schedule: {
    on: string;
    off: string;
    days: string;
    speed: string | number;
  };
  scheduleIndex: number;
  onSave: (
    schedule: { on: string; off: string; days: string; speed: string | number },
    index: number,
  ) => void;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

function parseTimeParts(timeStr: string): { hours: number; minutes: number } {
  if (timeStr.includes(":")) {
    const [h, m] = timeStr.split(":").map(Number);
    return { hours: h || 0, minutes: m || 0 };
  }
  const totalSeconds = parseInt(timeStr, 10) || 0;
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
  };
}

/** Local wall-clock Date — matches what PrimeReact Calendar displays. */
function timeStringToDate(timeStr: string): Date {
  const { hours, minutes } = parseTimeParts(timeStr);
  return new Date(1970, 0, 1, hours, minutes, 0, 0);
}

function dateToTimeString(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:00`;
}

/** Keep picker values as stable local wall-clock dates. */
function normalizePickerDate(date: Date): Date {
  return new Date(1970, 0, 1, date.getHours(), date.getMinutes(), 0, 0);
}

const speedOptions = [2, 4, 6, 8, 10];

const ScheduleEditModal: React.FC<ScheduleEditModalProps> = ({
  visible,
  onHide,
  schedule,
  scheduleIndex,
  onSave,
}) => {
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [speed, setSpeed] = useState<number>(0);

  useEffect(() => {
    if (visible && schedule) {
      setStartTime(timeStringToDate(schedule.on));
      setEndTime(timeStringToDate(schedule.off));

      const speedValue = Number(schedule.speed);
      const closestSpeed = speedOptions.reduce((prev, curr) =>
        Math.abs(curr - speedValue) < Math.abs(prev - speedValue) ? curr : prev,
      );
      setSpeed(closestSpeed);
    }
  }, [visible, schedule]);

  const handleSpeedChange = (value: number) => {
    const closestSpeed = speedOptions.reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
    );
    setSpeed(closestSpeed);
  };

  const handleSave = () => {
    if (!startTime || !endTime) return;
    trackEvent("schedule_edited");

    onSave(
      {
        on: dateToTimeString(startTime),
        off: dateToTimeString(endTime),
        days: schedule.days,
        speed: speed.toString(),
      },
      scheduleIndex,
    );
    onHide();
  };

  const footer = (
    <div>
      <Button
        type="button"
        label="Cancel"
        icon="pi pi-times"
        className="p-button-outlined"
        onClick={onHide}
      />
      <Button
        type="button"
        label="Save"
        icon="pi pi-check"
        severity="secondary"
        onClick={handleSave}
        disabled={!startTime || !endTime}
      />
    </div>
  );

  return (
    <Dialog
      header={`Edit Timer ${scheduleIndex + 1}`}
      visible={visible}
      style={{ width: "450px" }}
      footer={footer}
      onHide={onHide}
      dismissableMask
      closeOnEscape
    >
      <ModalContent>
        <FormGroup>
          <Label>Schedule Time</Label>
          <TimeFieldsContainer>
            <TimeField>
              <Label>Start Time</Label>
              <Calendar
                value={startTime}
                onChange={(e) => {
                  const value = e.value as Date | null;
                  setStartTime(value ? normalizePickerDate(value) : null);
                }}
                timeOnly
                hourFormat="24"
                placeholder="Select start time"
              />
            </TimeField>
            <TimeField>
              <Label>End Time</Label>
              <Calendar
                value={endTime}
                onChange={(e) => {
                  const value = e.value as Date | null;
                  setEndTime(value ? normalizePickerDate(value) : null);
                }}
                timeOnly
                hourFormat="24"
                placeholder="Select end time"
              />
            </TimeField>
          </TimeFieldsContainer>
        </FormGroup>

        <SpeedSliderContainer>
          <Label>Speed (2-10)</Label>
          <SpeedValue>{speed}</SpeedValue>
          <Slider
            value={speed}
            onChange={(e) => handleSpeedChange(e.value as number)}
            min={2}
            max={10}
            step={2}
            color="#FF4081"
            style={{ width: "100%" }}
          />
        </SpeedSliderContainer>
      </ModalContent>
    </Dialog>
  );
};

export default ScheduleEditModal;
